import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { disputesApi } from '../api';
import { useAuthStore } from '../store';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Send, Loader2 } from 'lucide-react';

const formatDate = (date) => new Date(date).toLocaleTimeString('en-NG', { hour: '2-digit', minute: '2-digit' });

export default function DisputeDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [dispute, setDispute] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => { fetchDispute(); }, [id]);
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [dispute?.messages]);

  const fetchDispute = async () => {
    try { const response = await disputesApi.getOne(id); setDispute(response.data); } 
    catch (error) { toast.error('Dispute not found'); navigate('/disputes'); } 
    finally { setIsLoading(false); }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;
    setSending(true);
    try { await disputesApi.addMessage(id, message); setMessage(''); fetchDispute(); } 
    catch (error) { toast.error('Failed to send message'); } 
    finally { setSending(false); }
  };

  if (isLoading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#FFD700]" /></div>;
  if (!dispute) return null;

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <Button variant="ghost" onClick={() => navigate('/disputes')} className="text-zinc-400 hover:text-white"><ArrowLeft className="w-4 h-4 mr-2" /> Back</Button>
      
      <div className="flex items-center justify-between">
        <div><h1 className="text-xl font-bold text-white font-['Outfit']">{dispute.title}</h1><p className="text-sm text-zinc-500">Order #{dispute.order_id.slice(0, 8)}</p></div>
        <Badge variant="outline" className={dispute.status === 'resolved' ? 'border-green-500/30 text-green-500' : dispute.status === 'closed' ? 'border-zinc-500/30 text-zinc-500' : 'border-yellow-500/30 text-yellow-500'}>{dispute.status}</Badge>
      </div>

      <Card className="bg-[#0A0A0A] border-white/10 h-[400px] flex flex-col">
        <CardContent className="flex-1 overflow-y-auto p-4 space-y-4">
          {dispute.messages?.map((msg) => (
            <div key={msg.id} className={`flex ${msg.user_id === user?.id ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] rounded-lg p-3 ${msg.is_admin ? 'bg-[#FFD700]/20 border border-[#FFD700]/30' : msg.user_id === user?.id ? 'bg-blue-600' : 'bg-[#121212]'}`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-xs font-semibold ${msg.is_admin ? 'text-[#FFD700]' : 'text-zinc-400'}`}>{msg.is_admin ? 'Admin' : msg.username}</span>
                  <span className="text-xs text-zinc-500">{formatDate(msg.created_at)}</span>
                </div>
                <p className="text-white text-sm">{msg.content}</p>
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </CardContent>

        {dispute.status === 'open' && (
          <form onSubmit={handleSend} className="p-4 border-t border-white/10 flex gap-2">
            <Input value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Type your message..." className="bg-[#121212] border-white/10 text-white" />
            <Button type="submit" disabled={sending} className="bg-[#FFD700] text-black hover:bg-[#E5C100]">{sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}</Button>
          </form>
        )}
      </Card>
    </div>
  );
}
